import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../constants/config';
import * as newsApi from '../api/news';
import { getProfile } from '../api/profile';
import styles from './News.module.css';

const formatDate = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleString('ru-RU', {
    day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

/**
 * Извлекает URL превью-изображения из ссылки на видео (YouTube, Rutube, VK Video).
 * Возвращает null, если ссылка не распознана.
 */
const getVideoThumbnail = (url) => {
  if (!url) return null;
  try {
    const u = new URL(url);

    // YouTube: youtube.com/watch?v=ID, youtu.be/ID, youtube.com/embed/ID
    if (u.hostname.includes('youtube.com') || u.hostname === 'youtu.be') {
      let videoId = null;
      if (u.hostname === 'youtu.be') {
        videoId = u.pathname.slice(1);
      } else if (u.pathname.startsWith('/embed/')) {
        videoId = u.pathname.split('/')[2];
      } else {
        videoId = u.searchParams.get('v');
      }
      if (videoId) {
        return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
      }
    }

    // Rutube: rutube.ru/video/ID/
    if (u.hostname.includes('rutube.ru') && u.pathname.startsWith('/video/')) {
      const videoId = u.pathname.split('/')[2];
      if (videoId) {
        return `https://pic.rutube.ru/video/${videoId}`;
      }
    }
  } catch {
    // невалидный URL
  }
  return null;
};

const authorName = (author, showAsAdmin) => {
  if (showAsAdmin) return 'Администратор';
  if (!author) return 'Администратор';
  const name = [author.first_name, author.last_name].filter(Boolean).join(' ');
  return name || author.email;
};

// Поддерживает [текст](url) и голые https?:// ссылки
const LINK_REGEX = /\[([^\]]+)\]\((https?:\/\/[^)]+)\)|(https?:\/\/[^\s]+)/g;

const renderTextWithLinks = (text) => {
  const result = [];
  let last = 0;
  let match;
  let key = 0;

  LINK_REGEX.lastIndex = 0;
  while ((match = LINK_REGEX.exec(text)) !== null) {
    if (match.index > last) {
      result.push(text.slice(last, match.index));
    }
    if (match[1] && match[2]) {
      // [текст](url)
      result.push(
        <a key={key++} href={match[2]} target="_blank" rel="noopener noreferrer" className={styles.inlineLink}>
          {match[1]}
        </a>
      );
    } else {
      // голая ссылка
      result.push(
        <a key={key++} href={match[3]} target="_blank" rel="noopener noreferrer" className={styles.inlineLink}>
          {match[3]}
        </a>
      );
    }
    last = match.index + match[0].length;
  }
  if (last < text.length) result.push(text.slice(last));
  return result;
};

const isSubscriptionActive = (payUntil) => {
  if (!payUntil) return false;
  return new Date(payUntil) > new Date();
};

// --- Компонент комментариев ---
const Comments = ({ post, token, currentUserId, isAdmin }) => {
  const [comments, setComments] = useState([]);
  const [text, setText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await newsApi.getComments(token, post.id);
      setComments(data.comments);
    } catch {
      // тихо
    } finally {
      setIsLoading(false);
    }
  }, [token, post.id]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setIsSending(true);
    setError('');
    try {
      const data = await newsApi.addComment(token, post.id, text.trim());
      setComments((prev) => [...prev, data.comment]);
      setText('');
    } catch (err) {
      setError(err.response?.data?.message || 'Ошибка при отправке');
    } finally {
      setIsSending(false);
    }
  };

  const handleDelete = async (commentId) => {
    try {
      await newsApi.deleteComment(token, post.id, commentId);
      setComments((prev) => prev.filter((c) => c.id !== commentId));
    } catch {
      // тихо
    }
  };

  return (
    <div className={styles.comments}>
      <h4 className={styles.commentsTitle}>
        Комментарии {comments.length > 0 && <span className={styles.commentCount}>{comments.length}</span>}
      </h4>

      {isLoading ? (
        <p className={styles.commentsLoading}>Загрузка...</p>
      ) : comments.length === 0 ? (
        <p className={styles.commentsEmpty}>Пока нет комментариев. Будьте первым!</p>
      ) : (
        <div className={styles.commentsList}>
          {comments.map((c) => (
            <div key={c.id} className={styles.comment}>
              <div className={styles.commentMeta}>
                <span className={styles.commentAuthor}>{authorName(c.author, false)}</span>
                <span className={styles.commentDate}>{formatDate(c.created_at)}</span>
                {(c.author?.id === currentUserId || isAdmin) && (
                  <button
                    className={styles.commentDelete}
                    onClick={() => handleDelete(c.id)}
                    title="Удалить"
                  >
                    ✕
                  </button>
                )}
              </div>
              <p className={styles.commentText}>{c.content}</p>
            </div>
          ))}
        </div>
      )}

      <form onSubmit={handleSubmit} className={styles.commentForm}>
        <textarea
          className={styles.commentInput}
          placeholder="Написать комментарий..."
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={2}
        />
        {error && <p className={styles.commentError}>{error}</p>}
        <button type="submit" className={styles.commentSubmit} disabled={isSending || !text.trim()}>
          {isSending ? 'Отправка...' : 'Отправить'}
        </button>
      </form>
    </div>
  );
};

// --- Карточка поста ---
const PostCard = ({ post, token, currentUserId, isAdmin }) => {
  const [showComments, setShowComments] = useState(false);

  return (
    <article className={styles.post}>
      <div className={styles.postHeader}>
        <div className={styles.postMeta}>
          {post.pinned && <span className={styles.pinnedBadge}>📌 Закреплено</span>}
          <span className={styles.postAuthor}>{authorName(post.author, post.show_as_admin)}</span>
          <span className={styles.postDate}>{formatDate(post.published_at || post.created_at)}</span>
        </div>
        <span className={`${styles.postType} ${styles[`type_${post.type}`]}`}>
          {post.type === 'text' ? 'Запись' : post.type === 'link' ? 'Ссылка' : 'Мероприятие'}
        </span>
      </div>

      <div className={styles.postBody}>
        {/* Текстовый контент */}
        {post.content && <p className={styles.postContent}>{renderTextWithLinks(post.content)}</p>}

        {/* Ссылка */}
        {post.type === 'link' && post.link_url && (
          <a
            href={post.link_url}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.linkCard}
          >
            {(post.link_image || getVideoThumbnail(post.link_url)) && (
              <img
                src={
                  post.link_image
                    ? post.link_image.startsWith('/uploads/')
                      ? `${API_URL}${post.link_image}`
                      : post.link_image
                    : getVideoThumbnail(post.link_url)
                }
                alt=""
                className={styles.linkImage}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            )}
            <div className={styles.linkInfo}>
              <span className={styles.linkTitle}>{post.link_title || post.link_url}</span>
              <span className={styles.linkUrl}>{post.link_url}</span>
            </div>
          </a>
        )}

        {/* Мероприятие */}
        {post.type === 'event' && (
          <div className={styles.eventCard}>
            {post.event_title && <h3 className={styles.eventTitle}>{post.event_title}</h3>}
            {post.event_date && (
              <div className={styles.eventMeta}>
                <span className={styles.eventIcon}>📅</span>
                {formatDate(post.event_date)}
              </div>
            )}
            {post.event_location && (
              <div className={styles.eventMeta}>
                <span className={styles.eventIcon}>📍</span>
                {post.event_location}
              </div>
            )}
          </div>
        )}
      </div>

      <div className={styles.postFooter}>
        <button
          className={styles.toggleComments}
          onClick={() => setShowComments((v) => !v)}
        >
          {showComments ? 'Скрыть комментарии' : `Комментарии (${post._count?.comments ?? 0})`}
        </button>
      </div>

      {showComments && (
        <Comments
          post={post}
          token={token}
          currentUserId={currentUserId}
          isAdmin={isAdmin}
        />
      )}
    </article>
  );
};

// --- Блок "нет подписки" ---
const SubscriptionGate = () => (
  <div className={styles.subscriptionGate}>
    <div className={styles.subscriptionIcon}>🔒</div>
    <h2 className={styles.subscriptionTitle}>Доступ только для участников клуба</h2>
    <p className={styles.subscriptionText}>
      Новости клуба доступны участникам с активной подпиской.
      Оформите подписку, чтобы читать публикации, участвовать в обсуждениях и получать приглашения на мероприятия.
    </p>
    <Link to="/payment" className={styles.subscriptionButton}>
      Оформить подписку — 2 000 ₽/мес
    </Link>
  </div>
);

// --- Главная страница новостей ---
const News = () => {
  const { token, user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [payUntil, setPayUntil] = useState(undefined);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (user?.is_admin) {
          // Администраторы видят новости без проверки подписки
          const data = await newsApi.getPosts(token);
          setPosts(data.posts);
          setPayUntil(null);
        } else {
          // Для обычных пользователей проверяем подписку параллельно с загрузкой постов
          const [profileData, postsData] = await Promise.all([
            getProfile(token),
            newsApi.getPosts(token),
          ]);
          setPayUntil(profileData.pay_until);
          setPosts(postsData.posts);
        }
      } catch {
        setError('Не удалось загрузить данные');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [token, user]);

  const hasActiveSubscription = user?.is_admin || isSubscriptionActive(payUntil);

  return (
    <div className={styles.page}>
      <div className="container">
        <div className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>Новости клуба</h1>
        </div>

        {isLoading && <p className={styles.loading}>Загрузка...</p>}
        {error && <p className={styles.error}>{error}</p>}

        {!isLoading && !error && !hasActiveSubscription && payUntil !== undefined && (
          <SubscriptionGate />
        )}

        {!isLoading && !error && hasActiveSubscription && (
          <>
            {posts.length === 0 && (
              <div className={styles.empty}>
                <p>Пока нет публикаций. Следите за обновлениями!</p>
              </div>
            )}

            <div className={styles.feed}>
              {posts.map((post) => (
                <PostCard
                  key={post.id}
                  post={post}
                  token={token}
                  currentUserId={user?.id}
                  isAdmin={user?.is_admin}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default News;
